import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import { ChevronLeft } from 'assets/icons';
import SlideOverHeader from 'components/SlideOver/component/header';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import StyledInput from 'components/styledInput/StyledInput';
import Button from 'components/button';
import Banner from 'components/banner/banner';
import {
  COMPANY_ADMIN_ROLE,
  companyRoleDescription,
  companyRoleOptions,
} from 'constants/companyRoles';
import { validateContactForm } from 'helper/companyContactForm';
import {
  getPerimeterErrorMessage,
  isCanceledRequest,
  isStalePerimeterError,
} from 'components/perimeter/perimeterErrors';
import {
  formatMemberName,
  haveSameMemberIds,
  isUnrestrictedRole,
} from 'components/perimeter/perimeterMembers';
import {
  MEMBER_PERIMETERS_PICKED,
  PERIMETER_MODALS,
} from 'components/perimeter/perimeterUsage';
import { fillText, perimeterText } from 'components/perimeter/perimeterText';
import {
  diffNotificationLevels,
  withPerimeterFallback,
} from 'components/perimeter/memberNotifications';
import CompanyContactPerimeters from './CompanyContactPerimeters';
import CompanyContactNotifications from './CompanyContactNotifications';
import useMemberNotifications from './useMemberNotifications';

// errorCode from the api → [field to highlight, message key]
const API_ERRORS = {
  EMAIL_INVALID: ['email', 'emailInvalid'],
  EMAIL_ALREADY_USED: ['email', 'emailTaken'],
  FIRSTNAME_INVALID: ['firstName', 'required'],
  LASTNAME_INVALID: ['lastName', 'required'],
  ROLE_INVALID: ['role', 'required'],
  ADMIN_ROLE_TAKEN: ['role', 'adminTaken'],
};

const PENDING_VALIDATION = 0;

// The default texts speak of one perimeter being edited; here the request
// carries the member's whole list.
const MEMBER_PERIMETER_ERROR_OVERRIDES = {
  PERIMETER_MEMBER_NOT_RESTRICTABLE: 'member.errors.notRestrictable',
  PERIMETER_NOT_FOUND: 'member.errors.notFound',
  INVALID_PERIMETER_ID: 'member.errors.notFound',
  PERIMETER_ARCHIVED: 'member.errors.archived',
};

// errorCode of the notifications route → text key under notifications.errors.
// MEMBER_NOT_FOUND closes the screen instead, as for the perimeters.
const NOTIFICATION_ERROR_KEYS = {
  PERIMETER_FORBIDDEN: 'forbidden',
  INVALID_PERIMETER_ID: 'companyNotFound',
  COMPANY_NOT_FOUND: 'companyNotFound',
  PERIMETER_MEMBER_INVALID: 'memberInvalid',
  NOTIFICATION_TYPE_INVALID: 'typeInvalid',
  NOTIFICATION_LEVEL_INVALID: 'levelInvalid',
};

const NO_PERIMETERS = { status: 'loading', active: [] };

const CompanyContactEditView = ({
  companyInfo,
  member,
  perimeters = NO_PERIMETERS,
  onBack,
  onSaved,
}) => {
  const { language, setModalData, modalDataReturn, setModalDataReturn } =
    UseContext();
  const { toggle } = useModal();
  const t = (key) => language.getValue(`companyContacts.edit.${key}`, '');
  const [form, setForm] = useState({
    firstName: member.user?.firstname ?? '',
    lastName: member.user?.lastname ?? '',
    email: member.user?.email ?? '',
    role: String(member.role ?? ''),
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // null until the admin touches the list: it then follows the loaded one.
  const [draftPerimeterIds, setDraftPerimeterIds] = useState(null);
  const [perimeterError, setPerimeterError] = useState('');
  // The answer of the last contact save and what it sent: after a failed
  // perimeter save, a retry with the same values only sends the perimeters.
  const lastSavedContact = useRef(null);
  const memberNotifications = useMemberNotifications(
    companyInfo?._id,
    member.user?._id,
  );
  // Only the subjects the admin touched, over the levels read.
  const [draftNotificationLevels, setDraftNotificationLevels] = useState({});
  const [notificationError, setNotificationError] = useState('');

  const memberId = String(member.user?._id ?? member.id_employee ?? '');
  const isPerimetersReady = perimeters.status === 'ready';
  const activePerimeters = useMemo(
    () => (isPerimetersReady ? perimeters.active : []),
    [isPerimetersReady, perimeters.active],
  );
  // No route gives a member's perimeters: they are the active perimeters that
  // list the member's account.
  const currentPerimeterIds = useMemo(
    () =>
      activePerimeters
        .filter((perimeter) =>
          (perimeter.members || []).some(
            (entry) => String(entry.id) === memberId,
          ),
        )
        .map((perimeter) => perimeter.id),
    [activePerimeters, memberId],
  );
  const selectedPerimeterIds = draftPerimeterIds ?? currentPerimeterIds;
  // In the order of the active list (sorted by name by the api); an id whose
  // perimeter is no longer active drops out, here and from the request.
  const selectedPerimeters = activePerimeters.filter((perimeter) =>
    selectedPerimeterIds.includes(perimeter.id),
  );
  const isUnrestricted = isUnrestrictedRole(form.role);

  // Judged on the perimeters block as shown, draft included; unknown (null)
  // until the perimeters are read. It only picks the hint: a member without a
  // perimeter keeps 'perimeter', which the api reads as 'all' until they are
  // attached to one (card 1306).
  const hasActivePerimeter = isPerimetersReady
    ? selectedPerimeters.length > 0
    : null;
  // The api reads 'all' where a Director or an Administrator stores
  // 'perimeter': the table shows it ahead, and the save only sends what the
  // admin changed on top.
  const isPerimeterLevelBlocked = isUnrestricted;
  const applyPerimeterRule = (levels) =>
    isPerimeterLevelBlocked ? withPerimeterFallback(levels) : levels;
  const isNotificationsReady = memberNotifications.status === 'ready';
  const shownNotificationLevels = isNotificationsReady
    ? applyPerimeterRule({
        ...memberNotifications.levels,
        ...draftNotificationLevels,
      })
    : null;
  // Nothing to send while the levels are not read: an unknown state is never
  // overwritten.
  const notificationPatch = isNotificationsReady
    ? diffNotificationLevels(
        applyPerimeterRule(memberNotifications.levels),
        shownNotificationLevels,
      )
    : {};
  const notificationHint = () => {
    if (isUnrestricted) return t('notifications.unrestrictedHint');
    if (hasActivePerimeter === false) return t('notifications.noPerimeterHint');
    return '';
  };

  const changeNotificationLevel = (type, level) => {
    setNotificationError('');
    setDraftNotificationLevels((previous) => ({ ...previous, [type]: level }));
  };

  useEffect(() => {
    if (modalDataReturn?.type !== MEMBER_PERIMETERS_PICKED) return;
    const pickedIds = modalDataReturn.perimeterIds || [];
    setModalDataReturn({});
    setPerimeterError('');
    setDraftPerimeterIds((previous) => {
      const base = previous ?? currentPerimeterIds;
      return [...base, ...pickedIds.filter((id) => !base.includes(id))];
    });
  }, [modalDataReturn, setModalDataReturn, currentPerimeterIds]);

  const openAddPerimeters = () => {
    setModalData({
      perimeters: activePerimeters.filter(
        (perimeter) => !selectedPerimeterIds.includes(perimeter.id),
      ),
      companyName: companyInfo?.name_commercial,
      memberName: formatMemberName(member.user),
    });
    toggle(PERIMETER_MODALS.addToMember);
  };

  const removePerimeter = (id) => {
    setPerimeterError('');
    setDraftPerimeterIds(selectedPerimeterIds.filter((kept) => kept !== id));
  };

  // The api refuses a second administrator whatever the other entry is, one
  // whose account is gone included; the warnings only count administrators
  // with an account, since an entry without one receives no reminder.
  const otherAdmins = useMemo(
    () =>
      (companyInfo?.employees ?? []).filter(
        (entry) =>
          Number(entry.role) === COMPANY_ADMIN_ROLE &&
          entry.user?._id !== member.user?._id,
      ),
    [companyInfo, member],
  );
  const otherAdminWithAccount = otherAdmins.find((entry) => entry.user);
  const isCompanyAdmin = Number(member.role) === COMPANY_ADMIN_ROLE;
  const normalizedEmail = form.email.trim().toLowerCase();
  // The warning is about the last administrator: with another one left, the
  // reminders of the company still reach someone.
  const isLastAdminLeaving =
    isCompanyAdmin &&
    !otherAdminWithAccount &&
    Number(form.role) !== COMPANY_ADMIN_ROLE;
  const isEmailChanged = normalizedEmail !== (member.user?.email ?? '');
  const isPending = member.user?.validation === PENDING_VALIDATION;

  // Never a message with an empty name: the holder can be an entry without an
  // account, or unknown to this screen when only the api saw them.
  const adminTakenText = () => {
    const name = [
      otherAdminWithAccount?.user?.firstname,
      otherAdminWithAccount?.user?.lastname,
    ]
      .filter(Boolean)
      .join(' ');
    if (name) return t('errors.adminTaken').replace('{name}', name);
    if (otherAdmins.length) return t('errors.adminTakenByMissingAccount');
    return t('errors.adminTakenUnknown');
  };

  const errorText = (key) => {
    if (!key) return undefined;
    if (key === 'adminTaken') return adminTakenText();
    return t(`errors.${key}`);
  };

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  // The invitation warning belongs to the contact save: a retry that only
  // resends the perimeters must not show it a second time.
  const warnInvitationOnce = (answer) => {
    const saved = lastSavedContact.current;
    if (answer.invitationResent !== false || saved?.isInvitationWarned) {
      return false;
    }
    if (saved?.answer === answer) saved.isInvitationWarned = true;
    toast.warning(t('savedInvitationFailed'));
    return true;
  };

  const finishSave = (answer) => {
    // Saved either way; false means the old link is dead and the new one
    // never left, which the admin can fix with the resend action.
    if (!warnInvitationOnce(answer)) toast.success(t('saved'));
    onSaved(answer.employee);
  };

  // The contact stays saved: the panel takes it in, the screen stays open on
  // the draft and says the perimeters did not follow.
  const keepOpenWithPerimeterError = (answer, reason) => {
    warnInvitationOnce(answer);
    onSaved(answer.employee, { keepOpen: true });
    setPerimeterError(
      fillText(perimeterText(language, 'member.saveFailed'), { reason }),
    );
  };

  // Runs once the perimeters are written, so that the levels the admin chose
  // are the last write.
  const saveNotifications = (answer) => {
    const levels = notificationPatch;
    if (Object.keys(levels).length === 0) {
      setIsSaving(false);
      finishSave(answer);
      return;
    }
    ApiDatabase.setAdminMemberNotifications(
      { idCompany: companyInfo._id, idEmployee: member.user._id, levels },
      (response) => {
        setIsSaving(false);
        setDraftNotificationLevels({});
        memberNotifications.setSavedLevels(response?.data);
        finishSave(answer);
      },
      (err) => {
        setIsSaving(false);
        if (isCanceledRequest(err)) return;
        console.error(err);
        const errorCode = err?.response?.data?.errorCode;
        if (errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        warnInvitationOnce(answer);
        onSaved(answer.employee, { keepOpen: true });
        const reasonKey = NOTIFICATION_ERROR_KEYS[errorCode] || 'generic';
        setNotificationError(
          fillText(t('notifications.saveFailed'), {
            reason: t(`notifications.errors.${reasonKey}`),
          }),
        );
      },
    );
  };

  // Runs once the contact is saved, so a Director or an Administrator is
  // judged on the role the api kept: those are never restricted, and the api
  // refuses them a list.
  const savePerimeters = (answer) => {
    const savedRole = answer.employee?.role ?? form.role;
    const perimeterIds = selectedPerimeters.map((perimeter) => perimeter.id);
    if (isUnrestrictedRole(savedRole)) {
      saveNotifications(answer);
      return;
    }
    // The lists failed to reload after the admin changed the draft: nothing
    // can be compared nor sent, which must not read as a full success.
    if (!isPerimetersReady && draftPerimeterIds !== null) {
      setIsSaving(false);
      keepOpenWithPerimeterError(
        answer,
        perimeterText(language, 'member.errors.notLoaded'),
      );
      perimeters.reload?.();
      return;
    }
    if (
      !isPerimetersReady ||
      haveSameMemberIds(perimeterIds, currentPerimeterIds)
    ) {
      saveNotifications(answer);
      return;
    }
    ApiDatabase.setAdminMemberPerimeters(
      { idCompany: companyInfo._id, idEmployee: member.user._id, perimeterIds },
      () => {
        perimeters.reload();
        saveNotifications(answer);
      },
      (err) => {
        setIsSaving(false);
        if (isCanceledRequest(err)) return;
        console.error(err);
        if (err?.response?.data?.errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        keepOpenWithPerimeterError(
          answer,
          getPerimeterErrorMessage(
            language,
            err,
            MEMBER_PERIMETER_ERROR_OVERRIDES,
          ),
        );
        if (isStalePerimeterError(err)) perimeters.reload();
      },
    );
  };

  const save = () => {
    const clientErrors = validateContactForm(form, {
      // A legacy company can have two administrators and the api lets each of
      // them keep the role: an administrator keeping it adds none, so only
      // someone else taking the role is refused.
      otherAdminExists: otherAdmins.length > 0 && !isCompanyAdmin,
    });
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }
    const employee = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: normalizedEmail,
      role: Number(form.role),
    };
    const sentContact = JSON.stringify(employee);
    setIsSaving(true);
    setPerimeterError('');
    setNotificationError('');
    if (lastSavedContact.current?.sent === sentContact) {
      savePerimeters(lastSavedContact.current.answer);
      return;
    }
    ApiDatabase.postEditCompanyMember(
      {
        idCompany: companyInfo._id,
        idEmployee: member.user._id,
        employee,
      },
      (data) => {
        lastSavedContact.current = { sent: sentContact, answer: data };
        savePerimeters(data);
      },
      (err) => {
        setIsSaving(false);
        if (err?.name === 'CanceledError') return;
        const errorCode = err?.response?.data?.errorCode;
        // Removed meanwhile: retrying cannot succeed, so the screen closes.
        if (errorCode === 'MEMBER_NOT_FOUND') {
          toast.error(t('errors.memberNotFound'));
          onBack();
          return;
        }
        const known = API_ERRORS[errorCode];
        if (known) {
          setErrors({ [known[0]]: known[1] });
          return;
        }
        toast.error(t('errors.generic'));
      },
    );
  };

  return (
    <>
      <SlideOverHeader hide={onBack} hideShow={false}>
        <div className={'text-sm mb-3'}>
          <button
            type={'button'}
            className={'border rounded p-2'}
            onClick={onBack}
          >
            <ChevronLeft wh={20} color={'#111827'} />
            <span className={'sr-only'}>{t('back')}</span>
          </button>
        </div>
        <div>{t('title')}</div>
        <div className={'text-gray-500 text-sm pt-0.5 font-normal'}>
          {t('subtitle')}
        </div>
      </SlideOverHeader>
      <SlideOverBody>
        <div className={'flex flex-col gap-4 text-sm'}>
          <div className={'grid grid-cols-2 gap-3'}>
            <StyledInput
              type={'text'}
              label={t('firstName')}
              value={form.firstName}
              onChange={update('firstName')}
              error={errorText(errors.firstName)}
            />
            <StyledInput
              type={'text'}
              label={t('lastName')}
              value={form.lastName}
              onChange={update('lastName')}
              error={errorText(errors.lastName)}
            />
          </div>
          <div className={'flex flex-col gap-1'}>
            <StyledInput
              type={'email'}
              label={t('email')}
              value={form.email}
              onChange={update('email')}
              error={errorText(errors.email)}
            />
            {isEmailChanged && !errors.email ? (
              <div className={'text-gray-500 text-xs'}>
                {t(isPending ? 'pendingEmailChange' : 'activeEmailHint')}
              </div>
            ) : null}
          </div>
          <StyledInput
            type={'select'}
            label={t('role')}
            list={companyRoleOptions(language)}
            value={form.role}
            onChange={update('role')}
            error={errorText(errors.role)}
            hint={companyRoleDescription(language, form.role)}
          />
          {isLastAdminLeaving ? (
            <Banner type={'warning'}>{t('lastAdminWarning')}</Banner>
          ) : null}
          <CompanyContactPerimeters
            status={perimeters.status}
            selectedPerimeters={selectedPerimeters}
            isUnrestricted={isUnrestricted}
            error={perimeterError}
            onAdd={openAddPerimeters}
            onRemove={removePerimeter}
          />
          <CompanyContactNotifications
            status={memberNotifications.status}
            levels={shownNotificationLevels}
            isPerimeterColumnDisabled={isPerimeterLevelBlocked}
            perimeterHint={notificationHint()}
            error={notificationError}
            onChange={changeNotificationLevel}
            onRetry={memberNotifications.reload}
          />
        </div>
      </SlideOverBody>
      <SlideOverFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={onBack}>
          {t('cancel')}
        </Button>
        <Button
          size={'LG'}
          color={isSaving ? 'DISABLED' : 'PRIMARY'}
          onClick={isSaving ? undefined : save}
        >
          {isSaving ? t('saving') : t('save')}
        </Button>
      </SlideOverFooter>
    </>
  );
};

export default CompanyContactEditView;
